import {Injectable} from '@angular/core';

import {normaliseLadder} from '../components/submit-po/encoding-ladder/encoding-ladder.component';
import {EncodingLadder, LADDER_PRESETS} from '../components/submit-po/encoding-ladder/encoding-ladder.model';

/**
 * The PO-wide encode settings of the `vod-default-cmaf-abr-v2` flow: everything under "Output
 * options" and nothing else.
 *
 * Deliberately not the whole PO form. Airline, project ID and the items are what *this* order is;
 * the watermark, encryption and ladder are how this operator encodes, and that answer is the same
 * order after order. Only the second half is worth carrying across a reload — a persisted PO
 * number or a persisted item list would be a stale order, not a preference.
 */
export interface VodEncodeSettings {
  /** Watermark, encryption, onboarding and thumbnails -- the format-specific card's value. */
  format_specific_data: any;
  encoding_ladder: EncodingLadder;
}

/**
 * Where the settings live in localStorage.
 *
 * Not scoped by organization: these are how an operator encodes, and an operator working two
 * airlines wants the same ladder in both. The one org-dependent key, `protection_systems`, is
 * pruned back to the template's default on submit whenever it still matches it.
 */
const STORAGE_KEY = 'vod-abr-v2-encode-settings';

/**
 * Stamped into every exported file and checked on import.
 *
 * A settings file is a bare JSON object, which is also what a stored PO's `format_specific_data`
 * looks like and what half the JSON on an operator's disk looks like. Without a marker, importing
 * the wrong file would half-apply -- unknown keys are dropped by `patchValue` and missing ones fall
 * back to defaults -- and produce a plausible-looking form that silently encodes something else.
 * The marker turns that into an error message.
 */
export const SETTINGS_FILE_FORMAT = 'castlabs.vod-abr-v2-encode-settings';

/**
 * Bumped only when an old file can no longer be read as-is. Nothing migrates yet: the two halves
 * are the same shapes the API receives, and both already tolerate an older PO (see
 * the schema form's `writeValue` and `EncodingLadderComponent.writeValue`).
 */
export const SETTINGS_FILE_VERSION = 1;

/** The exported file, and the shape import insists on. */
export interface VodEncodeSettingsFile {
  format: typeof SETTINGS_FILE_FORMAT;
  version: number;
  exported_at: string;
  settings: VodEncodeSettings;
}

@Injectable({providedIn: 'root'})
export class VodEncodeSettingsService {

  /**
   * What the form shows with nothing stored: no format-specific values (the schema form prefills the
   * template's defaults) and the first ladder preset.
   */
  static defaults(): VodEncodeSettings {
    return {
      // Nothing: the page's schema form prefills the template's own defaults.
      format_specific_data: {},
      encoding_ladder: LADDER_PRESETS[0].ladder,
    };
  }

  /**
   * The settings to open the form with -- stored ones if there are any readable ones, the defaults
   * otherwise.
   *
   * Never throws and never returns a half-read value: localStorage is shared with every other tab
   * and every past version of this app, so its contents are untrusted input. Anything unparseable
   * is dropped in favour of the defaults rather than reported, because there is nobody to report it
   * to at construction time and a broken key must not keep the page from rendering.
   */
  loadOrDefaults(): VodEncodeSettings {
    const raw = this._read();
    if (raw === null) {
      return VodEncodeSettingsService.defaults();
    }
    try {
      return VodEncodeSettingsService.sanitise(JSON.parse(raw));
    } catch (err) {
      console.warn(`Discarding unreadable ${STORAGE_KEY}`, err);
      return VodEncodeSettingsService.defaults();
    }
  }

  /** Is there anything stored? Only the "Forget stored settings" action needs to know. */
  hasStored(): boolean {
    return this._read() !== null;
  }

  /**
   * Persist the settings as they currently stand.
   *
   * The ladder is normalised first, for the same reason the submit path normalises it: a preset is
   * written with every key it is content to inherit left off, and storing it that way would restore
   * a ladder whose segment length is whatever the template picks rather than the one that was on
   * screen. Normalising also keeps the preset radio on the preset it was -- `_matchPreset` compares
   * normalised ladders.
   *
   * A full storage quota (or a browser with storage blocked) is not worth interrupting an operator
   * mid-PO for: the settings are a convenience, the form is unaffected, and the failure repeats on
   * every keystroke.
   */
  save(settings: VodEncodeSettings): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(VodEncodeSettingsService.sanitise(settings)));
    } catch (err) {
      console.warn(`Could not store ${STORAGE_KEY}`, err);
    }
  }

  /** Drop the stored settings. The caller decides what the form shows next. */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.warn(`Could not clear ${STORAGE_KEY}`, err);
    }
  }

  /** The file an export writes, pretty-printed so it can be diffed and hand-edited. */
  static toFile(settings: VodEncodeSettings, now: Date = new Date()): string {
    const file: VodEncodeSettingsFile = {
      format: SETTINGS_FILE_FORMAT,
      version: SETTINGS_FILE_VERSION,
      exported_at: now.toISOString(),
      settings: VodEncodeSettingsService.sanitise(settings),
    };
    return JSON.stringify(file, null, 2);
  }

  /**
   * Read an exported file back.
   *
   * Throws with a sentence an operator can act on, rather than returning null: every caller has a
   * message field to put it in, and "nothing happened" is the one outcome an import must never
   * have.
   */
  static fromFile(text: string): VodEncodeSettings {
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('Not a JSON file.');
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Not an encode settings file.');
    }
    if (parsed.format !== SETTINGS_FILE_FORMAT) {
      throw new Error('Not an encode settings file — it was not exported from this page.');
    }
    if (parsed.version > SETTINGS_FILE_VERSION) {
      throw new Error(`This file was written by a newer version of the page (v${parsed.version}). Update and try again.`);
    }
    if (!parsed.settings || typeof parsed.settings !== 'object') {
      throw new Error('The file carries no settings.');
    }
    try {
      return VodEncodeSettingsService.sanitise(parsed.settings);
    } catch (err) {
      // A file carrying the right marker but a ladder that is not a ladder -- hand-edited, or
      // written by a version whose shape has since changed beyond what `sanitise` absorbs.
      throw new Error(`The settings in this file could not be read: ${err}`);
    }
  }

  /** `<airline>`-free: settings are not tied to one order, so the name only carries the date. */
  static fileName(now: Date = new Date()): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      + `_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    return `vod-encode-settings_${stamp}.json`;
  }

  /**
   * Put an untrusted settings object into the shape the two cards can be handed.
   *
   * Both halves are merged onto the defaults rather than taken as they are, because a file or a
   * localStorage entry written by an older version of this page is missing whatever has been added
   * since -- and `setValue` on the PO form demands a complete value. The ladder cannot be merged
   * the same way (a ladder is a whole, not a set of independent keys), so a missing or non-object
   * one falls back to the default preset outright, which is what `EncodingLadderComponent` does
   * with a null anyway.
   */
  static sanitise(settings: any): VodEncodeSettings {
    const defaults = VodEncodeSettingsService.defaults();
    const fsd = settings?.format_specific_data;
    const ladder = settings?.encoding_ladder;
    const usable = ladder && typeof ladder === 'object' && !Array.isArray(ladder);
    return {
      format_specific_data: {
        ...defaults.format_specific_data,
        ...(fsd && typeof fsd === 'object' && !Array.isArray(fsd) ? migrateLegacyFsd(fsd) : {}),
      },
      encoding_ladder: normaliseLadder(usable ? ladder : defaults.encoding_ladder),
    };
  }

  private _read(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      // Safari in private mode, and any browser configured to block site data, throws on access.
      console.warn(`Could not read ${STORAGE_KEY}`, err);
      return null;
    }
  }
}

/**
 * Settings stored by the page before it rendered the template's schema carry three UI-only
 * spellings the old submit path translated and the schema form does not know. Translated here,
 * once, the way that path did, so a stored PO keeps submitting what it used to:
 * - `stardustmark.seed_auto: true` (the old form's seed checkbox) was submitted as `seed: -1`; without
 *   this the stored `seed: 0` would be sent as a fixed seed.
 * - `onboard_window_start` / `_end` (datetime-local strings, sent with `Z` appended) became
 *   `onboard_window: {start_time, end_time}`.
 * - `onboard_tags` as a comma-separated string became a list.
 */
/**
 * The DRM systems vod-default-cmaf-abr-v2 offers (its schema's protection_systems enum; a spec
 * checks the two agree). Only used to clean stored settings, never to build the form.
 */
export const V2_PROTECTION_SYSTEMS = ['Widevine', 'PlayReady', 'FairPlay'];

export function migrateLegacyFsd(fsd: any): any {
  const out = {...fsd};
  // Any other watermark spelling (v1's 'stardust/sf') was STARDUSTmark on this page: the only
  // watermark it ever offered. 'No Watermark' is the template's own spelling of none.
  if (out.watermark && out.watermark !== 'stardustmark' && out.watermark !== 'No Watermark') {
    out.watermark = 'stardustmark';
  }
  // Systems the template does not offer (e.g. Marlin) were dropped by the old form; none left
  // means the template's default set, as an absent key does.
  if (Array.isArray(out.protection_systems)) {
    const known = V2_PROTECTION_SYSTEMS.filter(p => out.protection_systems.includes(p));
    if (known.length) {
      out.protection_systems = known;
    } else {
      delete out.protection_systems;
    }
  }
  const sm = out.stardustmark;
  if (sm && typeof sm === 'object' && 'seed_auto' in sm) {
    const {seed_auto, ...rest} = sm;
    out.stardustmark = seed_auto ? {...rest, seed: -1} : rest;
  }
  const toUtc = (v: string) => v.split(':').length === 2 ? `${v}:00Z` : `${v}Z`;
  if ('onboard_window_start' in out || 'onboard_window_end' in out) {
    const {onboard_window_start: start, onboard_window_end: end, ...rest} = out;
    if (start && end && !rest.onboard_window) {
      rest.onboard_window = {start_time: toUtc(start), end_time: toUtc(end)};
    }
    return migrateTags(rest);
  }
  return migrateTags(out);
}

function migrateTags(fsd: any): any {
  if (typeof fsd.onboard_tags === 'string') {
    return {...fsd, onboard_tags: fsd.onboard_tags.split(',').map((t: string) => t.trim()).filter((t: string) => t)};
  }
  return fsd;
}
